import {
  Auth,
  SSNStatus,
  logout as authLogout,
  canUseMagicLogin,
  cancelSignInWithBankId,
  changeMarket,
  checkUserEmailVerified,
  confirmSignIn,
  confirmSignUp,
  forgotPassword,
  forgotPasswordSubmit,
  getAccessToken,
  getNextQrCode,
  getParsedClaims,
  magicLogin,
  onBankIdSignInStateChange,
  refreshTokens,
  resendMFA,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signUp,
  startBankIdSignIn,
  startPasskeyAuthentication,
  startPasskeyRegistration,
  updateCustomLanguageTagInCognito,
} from "@mindlercare/auth";
import {
  CodeISO2,
  DevApiUrls,
  JWTClaims,
  JWTClaimsRole,
  LanguageCodeISO2,
} from "@mindlercare/schema";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useIdleTimer } from "./hooks/useIdleTimer";
import { useIsPasskeySupported } from "./hooks/useIsPasskeySupported";
import { useStateNavigator } from "./hooks/useStateNavigator";
import { checkPasskeyOptOut } from "./components/PasskeyOptOutToggle";

const TWO_HOURS_IN_MS = 2 * 60 * 60 * 1000;

const defaultMarketLanguages: Record<CodeISO2, LanguageCodeISO2> = {
  SE: "SV",
  NL: "NL",
  GB: "EN",
  DK: "DA",
  NO: "NO",
};

type JWTRole = {
  name: JWTClaimsRole;
  roleId: number;
  bit: number;
};

type AuthState =
  | AuthStateLoggedIn
  | AuthStateLoginInProgress
  | AuthStateLoggedOut
  | AuthStateLoginBlocked;

type AuthStateLoggedIn = {
  status: "logged-in";
  jwt: string;
  roles: JWTRole[];
  marketId: number;
  isLoggedIn: true;
  userId: number;
};

type AuthStateLoginBlocked = {
  status: "login-blocked";
  jwt: string;
  roles: JWTRole[];
  marketId: number;
  isLoggedIn: false;
  userId: number;
};

type AuthStateLoginInProgress = {
  status: "login-in-progress";
  jwt: null;
  roles: null;
  marketId: number;
  isLoggedIn: false;
  userId: number;
};

type AuthStateLoggedOut = {
  status: "logged-out";
  isLoggedIn: false;
  jwt: null;
  roles: null;
  marketId: null;
  userId: null;
};

const authInitialState: AuthState = {
  status: "logged-out",
  jwt: null,
  roles: null,
  marketId: null,
  isLoggedIn: false,
  userId: null,
};

export const AuthContext = createContext<Auth | null>(null);

export type AuthenticatorSettings = {
  locale?: string;
};

export const AuthenticatorSettingsContext =
  createContext<AuthenticatorSettings | null>(null);

type AuthStateContextFunctions = {
  logout: () => void;
  refresh: () => Promise<string | null>;
  switchMarket: (market: CodeISO2) => void;
  setPendingStatus: () => void;
  resetState: () => void;
};

type AuthStateContextVariables = {
  selectedMarket: CodeISO2;
  passkeySupported: boolean;
};

export const AuthStateContext = createContext<
  (AuthState & AuthStateContextFunctions & AuthStateContextVariables) | null
>(null);

type JWTLoginClaims = Extract<JWTClaims, { type: "LOGIN" }>;
export type AuthenticationProviderProps = {
  auth: Auth;
  locale?: string;
  children: ReactNode;
  /**
   * The maximum amount of time in milliseconds a user is
   * allowed to be idle (after token has expired) before we automatically logout.
   *
   * Here idle means one of the following:
   * - User closed the application and opens it again
   *
   * Default is 2 hours after token expires
   */
  maxUserIdleMS?: number;
  blockLoginWhen: (claims: JWTLoginClaims) => boolean;
  logoutCallback?: () => void;
  allowPasskeys?: boolean;
};

const isJWTLoginClaims = (claims: JWTClaims): claims is JWTLoginClaims => {
  return claims.type === "LOGIN";
};

/**
 * Check whether token expiration timestamp has passed.
 * @param expireAt Timestamp (in seconds) when token will expire.
 * @returns Whether token has expired
 */
const isExpired = (expireAt: number) => {
  return Date.now() > expireAt * 1000;
};

const calcRefreshDelay = async (auth: Auth) => {
  const parsedClaims = await getParsedClaims(auth);
  if (!parsedClaims) {
    throw Error("The must be a jwt for refresh");
  }

  const now = Date.now();
  const expireMS = parsedClaims.exp * 1000;
  const oneMinute = 1000 * 60;
  const delay = expireMS - now < oneMinute ? 0 : (expireMS - now) / 2;
  return delay;
};

type SetAuthneticatedStateOpts = {
  registerPasskey?: boolean;
};

export const AuthenticationProvider = ({
  auth,
  locale,
  children,
  blockLoginWhen,
  maxUserIdleMS: maxUserIdleMSFromProp,
  logoutCallback,
  allowPasskeys = true,
}: AuthenticationProviderProps) => {
  const { navigate } = useStateNavigator();
  const [state, setState] = useReducer<
    (state: AuthState, payload: Partial<AuthState>) => AuthState
  >((prev, payload): AuthState => {
    return {
      ...prev,
      ...payload,
    } as AuthState;
  }, authInitialState);
  const [selectedMarket, setSelectedMarket] = useState(
    auth?.config.market ?? "SE",
  );

  const passkeySupported = useIsPasskeySupported() && allowPasskeys;

  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setAuthenticatedState = useCallback(
    async (opts?: SetAuthneticatedStateOpts) => {
      const jwt = await getAccessToken(auth);
      const parsedClaims = await getParsedClaims(auth);

      if (!jwt || !parsedClaims) {
        throw Error("Must be jwt and parsed claims");
      }

      const parsedRoles = isJWTLoginClaims(parsedClaims)
        ? parsedClaims.roles
        : [];

      if (isJWTLoginClaims(parsedClaims) && blockLoginWhen(parsedClaims)) {
        setState({
          status: "login-blocked",
          isLoggedIn: false,
          userId: parsedClaims.userId,
          roles: parsedClaims.roles as JWTRole[],
          marketId: Number(parsedClaims.market.countryId),
        });
        navigate({ view: "login-blocked", market: selectedMarket });
        return;
      }

      setState({
        status: "logged-in",
        isLoggedIn: true,
        jwt,
        roles: parsedRoles as JWTRole[],
        marketId: Number(parsedClaims.market.countryId),
        userId: parsedClaims.userId,
      });

      if (passkeySupported && opts?.registerPasskey) {
        await startPasskeyRegistration(auth);
      }
    },
    [blockLoginWhen, passkeySupported, selectedMarket, navigate, auth],
  );

  const setPendingStatus = useCallback(() => {
    setState({
      status: "login-in-progress",
    });
  }, []);

  const resetState = useCallback(() => {
    setState(authInitialState);
  }, []);

  const logout = useCallback(async () => {
    await authLogout(auth);
    setState(authInitialState);
    if (logoutCallback) {
      logoutCallback();
    }
  }, [auth, logoutCallback]);

  const switchMarket = useCallback(
    async (market: CodeISO2) => {
      const switchFromXGtoSE = market === "SE" && auth.config.market !== "SE";
      const switchFromSEtoXG = market !== "SE" && auth.config.market === "SE";
      // since SE is in one VPC region, while all other markets are in another,
      // we need to logout when switching between them, since SE users are not
      // in XG and vice versa
      if (switchFromSEtoXG || switchFromXGtoSE) {
        await logout();
      }

      changeMarket(auth, market);
      setSelectedMarket(market);
    },
    [auth, logout],
  );

  const refresh = useCallback(async () => {
    try {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }

      await refreshTokens(auth);

      await setAuthenticatedState();
      refreshTimeoutRef.current = setTimeout(
        refresh,
        await calcRefreshDelay(auth),
      );
    } catch {
      const { exp } = (await getParsedClaims(auth)) ?? {};
      if (isExpired(exp ?? 0)) {
        await logout();
      }
    }

    return getAccessToken(auth);
  }, [auth, setAuthenticatedState, logout]);

  const handlePotentiallyExpiredJWT = useCallback(async () => {
    const user = await getParsedClaims(auth);
    if (!user) {
      return;
    }
    const elapsedSinceExpirationMS = Date.now() - user.exp * 1000;
    // This two hours check is just arbitrarily set.
    // The refresh token does not actually ever expire so we could increase it. However, we want something reasonable as MC for example contains medical data.
    const maxUserIdleMS = maxUserIdleMSFromProp ?? TWO_HOURS_IN_MS;
    if (elapsedSinceExpirationMS > maxUserIdleMS) {
      console.info(
        `Logged out because access token exp has passed ${elapsedSinceExpirationMS} ms`,
      );
      await logout();
      return;
    }
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    refreshTimeoutRef.current = setTimeout(
      refresh,
      await calcRefreshDelay(auth),
    );
  }, [logout, maxUserIdleMSFromProp, refresh, auth]);

  // This use effect catches what happens when the user just "opens the lid" and has MC open from before
  // The reason we need it is because auth-lib is not rerendered in this case and thus the onAuthStatusChanged is never triggered
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handlePotentiallyExpiredJWT();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [handlePotentiallyExpiredJWT]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStatusChanged(async (status) => {
      if (status.status === "anonymous") {
        setState(authInitialState);
        return;
      }

      if (status.status === "authenticated") {
        // if status.method is undefined, it probably was a refresh of the page
        // We also do not want to register passkey if we are logging in with passkey
        const shouldRegisterOrUpdatePasskey =
          status.method !== undefined &&
          status.method !== "passkey" &&
          !checkPasskeyOptOut();
        await setAuthenticatedState({
          registerPasskey: shouldRegisterOrUpdatePasskey,
        });
        await handlePotentiallyExpiredJWT();
      }
    });

    return () => unsubscribe();
  }, [
    auth,
    blockLoginWhen,
    handlePotentiallyExpiredJWT,
    setAuthenticatedState,
  ]);

  const settings = useMemo<AuthenticatorSettings | null>(
    () =>
      auth
        ? {
            locale,
          }
        : null,
    [auth, locale],
  );

  useIdleTimer({
    onIdle: async () => {
      await logout();
    },
    idleTimeSeconds: 60 * 60 * 2, // two hours of inactive => log out automatically
  });

  return (
    <AuthContext.Provider value={auth}>
      <AuthenticatorSettingsContext.Provider value={settings}>
        <AuthStateContext.Provider
          value={{
            ...state,
            logout,
            refresh,
            selectedMarket,
            switchMarket,
            setPendingStatus,
            resetState,
            passkeySupported,
          }}
        >
          {children}
        </AuthStateContext.Provider>
      </AuthenticatorSettingsContext.Provider>
    </AuthContext.Provider>
  );
};

export const useInternalAuth = () => {
  const context = useContext(AuthContext);
  const state = useContext(AuthStateContext);

  if (!context || !state) {
    throw new Error("useAuth must be used inside AuthContextProvider.");
  }

  return {
    status: state.status,
    market: state.selectedMarket,
    passkeySupported: state.passkeySupported,
    sendEmailVerification: () => sendEmailVerification(context),
    isUserEmailVerified: () => checkUserEmailVerified(context),
    updateCustomLanguageTagInCognito: () =>
      updateCustomLanguageTagInCognito(
        context,
        defaultMarketLanguages[state.selectedMarket],
      ),
    resetState: state.resetState,
    setPendingStatus: state.setPendingStatus,
    switchMarket: state.switchMarket,
    getNextQrCode: () => getNextQrCode(context),
    canUseMagicLogin: () => canUseMagicLogin(context),
    magicLogin: (input: string, options?: DevApiUrls) =>
      magicLogin(context, input, state.selectedMarket, options),
    onAuthStatusChange: context.onAuthStatusChanged,
    startPasskeyAuthentication: () => {
      state.setPendingStatus();
      return startPasskeyAuthentication(context);
    },
    startPasskeyRegistration: () => startPasskeyRegistration(context),
    startBankIdSignIn: () => startBankIdSignIn(context),
    cancelSignInWithBankId: () => cancelSignInWithBankId(context),
    onBankIdSignInStateChange: (next: (state: SSNStatus) => void) =>
      onBankIdSignInStateChange(context, next),
    loginWithEmailAndPassword: async (email: string, password: string) => {
      // Somehow we still experience situations when there is stale Cognito session
      // lying in the localStorage, so this logout ensures that we are always in a clean state.
      if (state.selectedMarket !== "SE") {
        await authLogout(context);
      }

      return signInWithEmailAndPassword(context, email, password);
    },
    forgotPassword: (email: string) => forgotPassword(context, email),
    forgotPasswordSubmit: (email: string, code: string, password: string) =>
      forgotPasswordSubmit(context, email, code, password),
    confirmSignIn: (code: string) => confirmSignIn(context, code),
    signUp: (email: string, password: string, phoneNumber: string) =>
      signUp(context, email, password, phoneNumber, {
        market: state.selectedMarket,
        language: defaultMarketLanguages[state.selectedMarket],
      }),
    confirmSignUp: (email: string, code: string) =>
      confirmSignUp(context, email, code),
    resendMFA: () => resendMFA(context),
  };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  const state = useContext(AuthStateContext);

  if (!context || !state) {
    throw new Error("useAuth must be used inside AuthContextProvider.");
  }

  return {
    jwt: state.jwt,
    roles: state.roles,
    marketId: state.marketId,
    userId: state.userId,
    isLoggedIn: state.isLoggedIn,
    logout: state.logout,
    refresh: state.refresh,
    switchMarket: state.switchMarket,
    market: state.selectedMarket,
    loginState: state.status,
    resetState: state.resetState,
  };
};

export const useAuthenticationSettings = () => {
  const context = useContext(AuthenticatorSettingsContext);

  if (!context) {
    throw new Error(
      "useAuthenticationSettings must be used inside AuthenticationProvider.",
    );
  }

  return context;
};
